/**
 * Domain types. These mirror the Firestore documents used by the prod admin and
 * the reader app, so the data shape stays compatible with the gateway responses.
 */

export const SUPERADMIN_ROLE = "Superadmin";

/** A publisher admin account. Doc id === Firebase Auth uid. */
export interface Admin {
  /** Firebase Auth uid (= Firestore doc id) */
  id: string;
  isGrid: boolean;
  /** Publishing house doc id this admin drives, e.g. "stylet_i_stylos" */
  publishingHouseId: string;
  /** e.g. "Superadmin"; absent for regular publishers */
  role: string | null;
}

export interface Purchase {
  bookId: string;
  /** ISO string */
  date: string;
}

export interface PublishingHouse {
  id: string;
  name: string;
  logoUrl: string;
  bannerUrl: string;
  revenuePercent: number | null;
  purchases: Purchase[];
}

export interface Book {
  /** store doc id */
  id: string;
  name: string;
  authors: string[];
  cover: string;
  coverFullSize: string;
  licenseURL: string;
  demoURL: string;
  /** genre ids (normalised from ref or string) */
  genres: string[];
  priceId: string;
  description: string;
  descriptionHTML: string;
  /** publishing house NAME */
  publishingHouse: string;
  /** URL slug derived from the book title */
  transliteratedName: string;
  isbn: string;
  recommendedPrice: string;
  /** ISO string | "" */
  publishingDate: string;
  isPublic: boolean;
  showDemo: boolean;
  type?: "ebook" | "audiobook";
  /** ISO string | "" */
  createdAt: string;
  /** ISO string | "" */
  rightsValidityTerm: string;
  audioBookMap: Record<string, unknown> | null;
  /** publication id from the LCP pipeline (newer books) */
  lcpId: string;
  /** legacy book-file URL (older books stored the file under `url`); read-only */
  fileUrl: string;
  /** sibling documents of the other format, linked via Firestore `relations` */
  relations: { id: string; type: "ebook" | "audiobook" }[];
  chapters?: string[];
}

export interface Genre {
  id: string;
  name: string;
}

/** Summary of a publishing house for the superadmin Publishers list. */
export interface PublisherSummary {
  id: string;
  name: string;
  logoUrl: string;
  /** fraction, e.g. 0.7 → 70% */
  revenuePercent: number | null;
}

export interface CampaignVisit {
  appVersion: string;
  platform: string;
  /** ISO string */
  date: string;
}

export interface Campaign {
  /** doc id (= the linked entity id, e.g. a book id) */
  id: string;
  name: string;
  deeplinkUrl: string;
  publisher: string;
  /** ISO string | "" */
  createdAt: string;
  visits: CampaignVisit[];
}

export interface Invoice {
  bookId: string;
  bookName: string;
  authors: string;
  isbn: string;
  publishingHouse: string;
  platform: string;
  amount: number;
  /** amount × publisher.revenuePercent */
  revenue: number;
  /** ISO string */
  transactionDate: string;
  transactionId: string;
  status: string;
}

export interface PriceTier {
  /** doc id, e.g. "eight_usd_book" — stored as Book.priceId */
  tierName: string;
  priceUSD: number;
  priceUAH: number;
}

export type ApiResponse<T> =
  | { success: true; data: T }
  | { success: false; error: string; errorCode?: string };
